import { t } from '@framework/i18n/intl/i18next.ts';
import { yup } from '@framework/validation/initYup.ts';
import { RuleType } from '../../IRule.ts';
import { type IRuleType } from '../../IRuleType.ts';
import { TextFormatRuleComponent } from './TextFormatRuleComponent.tsx';

export enum TextFormat {
  FiscalCode = 'fiscalCode',
  Number = 'number',
  Phone = 'phone',
  Custom = 'custom',
  Email = 'email',
  Piva = 'piva',
}

const configValidator = yup
  .object({
    format: yup
      .mixed<TextFormat>()
      .oneOf(Object.values(TextFormat), '${label} non riconosciuto.')
      .notRequired()
      .label('Formato testo'),
    customFormat: yup
      .string()
      .notRequired()
      .when('format', ([format]: TextFormat[], schema) => {
        return format === TextFormat.Custom
          ? schema.required('È obbligatorio indicare il ${label}')
          : schema.notRequired().strip();
      })
      .label('Formato personalizzato'),
    customMessage: yup
      .string()
      .notRequired()
      .nullable()
      .label('Messaggio personalizzato di validazione'),
  })
  .notRequired()
  .nullable()
  .noUnknown();

export type IRuleTextFormatType = yup.InferType<typeof configValidator>;

export const TextFormatRule: IRuleType<IRuleTextFormatType> = {
  type: RuleType.TextFormat,
  name: 'Formato Testo',
  configComponent: TextFormatRuleComponent,
  configValidator,
  validator: (schema, rule) => {
    if (!rule) return schema;

    const result = schema as yup.StringSchema;

    switch (rule.format) {
      case TextFormat.FiscalCode:
        return result.fiscalNumber(rule.customMessage ?? undefined);
      case TextFormat.Number:
        return result.test(
          'number-format',
          rule.customMessage ??
            t('rules.text-format.number', 'Il campo ${label} non è un numero.'),
          value => {
            if (!value) return true;
            return /^-?[0-9]+$/.test(value);
          },
        );
      case TextFormat.Phone:
        return result.test(
          'phone-format',
          rule.customMessage ??
            t(
              'rules.text-format.phone',
              'Il campo ${label} non è un numero di telefono.',
            ),
          value => {
            if (!value) return true;
            return /^[0-9+ ]+$/.test(value);
          },
        );
      case TextFormat.Email:
        return result.email(rule.customMessage ?? undefined);
      case TextFormat.Piva:
        return result.vatNumber(rule.customMessage ?? undefined);
      case TextFormat.Custom:
        if (rule.customFormat) {
          return result.test(
            'custom-format',
            rule.customMessage ?? '',
            value => {
              if (!value) return true;
              return new RegExp(rule.customFormat!).test(value);
            },
          );
        }
        return result;
      default:
        return result;
    }
  },
};
